// Phase 1 Worker（server/src/index.ts）への薄いクライアント。
// 認証は PF34 Bearer（session）優先・ローカルは X-Dev-User（Slice 5）。
import { config } from "../config";
import { currentToken, clearSession } from "../auth/session";
import { buildAuthHeaders } from "../auth/session-core.mjs";
import { sheetsAuthHeaders } from "../auth/sheets-token";
import type { AppDefinition, Row } from "../types";

function authHeaders(): Record<string, string> {
  // Bearer（ログイン session）優先。無ければ env の bearer（テスト用）→ ローカルの devUser。
  return buildAuthHeaders({ token: currentToken() ?? config.bearer, devUser: config.devUser });
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${config.apiBase}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (res.status === 401) {
    clearSession(); // 失効 → ログイン UI へ戻す（PF34）
    throw new Error("ログインが必要です");
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    const msg = (body.error as string) ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body as T;
}

/** 自組織のアプリ一覧（GET /apps）。ビルダー（Theme Studio）の「開く」導線に使う。 */
export async function listApps(): Promise<Array<{ id: string; name?: string; draft_version?: number }>> {
  const r = await req<{ ok: true; apps: Array<{ id: string; name?: string; draft_version?: number }> }>("/apps");
  return r.apps ?? [];
}

/** 下書きを保存（PUT /apps/:id/draft）。ビルダーの編集を Worker に書く。 */
export async function putDraft(app: AppDefinition): Promise<{ version: number }> {
  const r = await req<{ ok: true; version: number }>(
    `/apps/${encodeURIComponent(app.id)}/draft`,
    { method: "PUT", body: JSON.stringify({ app }) }
  );
  return { version: r.version };
}

/** 下書きを検証して公開（POST /apps/:id/publish）。検証失敗(422)は errors を含めて投げる。 */
export async function publishApp(appId: string): Promise<{ seq: number; version: number; warnings: string[] }> {
  const res = await fetch(`${config.apiBase}/apps/${encodeURIComponent(appId)}/publish`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  if (res.status === 401) { clearSession(); throw new Error("ログインが必要です"); }
  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean; seq?: number; version?: number; warnings?: string[]; error?: string; errors?: string[];
  };
  if (!res.ok || body.ok === false) {
    const detail = body.errors?.length ? `: ${body.errors.join(" / ")}` : "";
    throw new Error((body.error ?? `HTTP ${res.status}`) + detail);
  }
  return { seq: body.seq ?? 0, version: body.version ?? 0, warnings: body.warnings ?? [] };
}

/** 公開スナップショットの最新版を取得（GET /apps/:id/published/latest）。 */
export async function getPublishedApp(appId: string): Promise<AppDefinition> {
  const r = await req<{ ok: true; app: AppDefinition; seq: number; version: number }>(
    `/apps/${encodeURIComponent(appId)}/published/latest`
  );
  return r.app;
}

/** 下書きの取得（公開前の確認用。GET /apps/:id/draft）。 */
export async function getDraftApp(appId: string): Promise<AppDefinition> {
  const r = await req<{ ok: true; app: AppDefinition; version: number }>(
    `/apps/${encodeURIComponent(appId)}/draft`
  );
  return r.app;
}

/**
 * テーブルの行を読む（POST /data/:appId/:tableRef/read）。B-2 権限フィルタはサーバで適用済み。
 * dataSource が google-sheets のテーブルは X-Sheets-Token（Slice 5b・B1）が必要
 * （未取得なら「Sheets連携のトークンが必要です」をサーバが返す＝エラーメッセージで判別）。
 */
export async function readRows(appId: string, tableRef: string): Promise<Row[]> {
  const r = await req<{ ok: true; rows: Row[] }>(
    `/data/${encodeURIComponent(appId)}/${encodeURIComponent(tableRef)}/read`,
    { method: "POST", body: "{}", headers: sheetsAuthHeaders() }
  );
  return r.rows ?? [];
}

/** サーバ側書込結果（server/src/data.ts writeRows の各要素）。 */
export type WriteResult =
  | { ok: true; rowId: string; changed?: string[] }
  | { ok: false; rowId?: string; errorCode: string; error?: string; conflicts?: string[]; current?: Record<string, unknown>; version?: number };

export type Mutation = {
  op: "create" | "update" | "delete";
  rowId?: string;
  values?: Record<string, unknown>;
  base?: Record<string, unknown>;
  baseVersion?: number;
  idempotencyKey: string;
};

/** 行を書き込む（POST /data/:appId/:tableRef/write）。native=冪等＋サーバ権威競合判定(D-0034)／google-sheets=B1トークン(Slice 5b)。 */
export async function writeRows(appId: string, tableRef: string, mutations: Mutation[]): Promise<WriteResult[]> {
  const r = await req<{ ok: true; results: WriteResult[] }>(
    `/data/${encodeURIComponent(appId)}/${encodeURIComponent(tableRef)}/write`,
    { method: "POST", body: JSON.stringify({ mutations }), headers: sheetsAuthHeaders() }
  );
  return r.results ?? [];
}
